import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import Home from "./pages/home";
import Search from "./pages/search";
import StudyDetails from "./pages/studyDetails";
import Category from "./pages/category";
import Login from "./pages/login";
import Admin from "./pages/admin";
import Register from "./pages/register";
import Favorites from "./pages/favorites";
import Profile from "./pages/profile";
import Heatmap from "./pages/heatmap";
import SeizureBiomeDB from "./pages/about/seizureBiomeDB";
import Statistics from "./pages/about/statistics";
import Guide from "./pages/about/guide";
import Contact from "./pages/about/contact";

function App() {
  const [showDropdown, setShowDropdown] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const navRef = useRef(null);

  // Check auth on mount
  useEffect(() => {
    fetch("/api/me", { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(u => setCurrentUser(u))
      .catch(() => setCurrentUser(null));
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setShowDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = async () => {
    await fetch("/api/logout", { method: "POST", credentials: "include" });
    setCurrentUser(null);
    window.location.href = "/";
  };

  const navContainerStyle = {
    background: "#0a2540",
    padding: "0.75rem 2rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    position: "sticky",
    top: 0,
    zIndex: 1000,
  };

  const logoStyle = { height: "36px" };

  const navStyle = {
    display: "flex",
    gap: "0.25rem",
    position: "relative",
    alignItems: "center",
  };

  const linkStyle = {
    color: "#c8dff0",
    fontWeight: "500",
    textDecoration: "none",
    cursor: "pointer",
    padding: "0.45rem 0.85rem",
    borderRadius: "5px",
    transition: "background 0.15s, color 0.15s",
    position: "relative",
    fontSize: "0.9rem",
  };

  const dropdownContainerStyle = {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    background: "#fff",
    border: "1px solid #e2e8f0",
    padding: "0.35rem 0",
    display: "flex",
    flexDirection: "column",
    zIndex: 1000,
    minWidth: "200px",
    borderRadius: "8px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  };

  const dropdownItemStyle = {
    color: "#1e293b",
    background: "none",
    border: "none",
    borderRadius: 0,
    fontWeight: 400,
    fontSize: "0.875rem",
    textDecoration: "none",
    padding: "0.55rem 1rem",
    display: "block",
    transition: "background 0.12s",
    textAlign: "left",
    cursor: "pointer",
    width: "100%",
  };

  const toggleDropdown = (type) => setShowDropdown(prev => prev === type ? null : type);
  const handleMouseOver = (e) => { e.target.style.background = "#f1f5f9"; };
  const handleMouseOut  = (e) => { e.target.style.background = "none"; };
  const linkHoverStyle  = { background: "rgba(255,255,255,0.1)", color: "#fff" };

  const displayName = currentUser
    ? (currentUser.first_name || currentUser.username)
    : null;

  return (
    <Router>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        style={navContainerStyle}
      >
        <Link to="/">
          <motion.img
            src="/navlogo.png"
            alt="SeizureBiomeDB Logo"
            style={logoStyle}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          />
        </Link>

        <nav style={navStyle} ref={navRef}>
          {/* Home */}
          <motion.div whileHover={linkHoverStyle} transition={{ duration: 0.15 }}>
            <Link to="/" style={linkStyle}>Home</Link>
          </motion.div>

          {/* Search */}
          <motion.div whileHover={linkHoverStyle} transition={{ duration: 0.15 }}>
            <Link to="/search" style={linkStyle}>Search</Link>
          </motion.div>

          {/* Browse */}
          <div style={{ position: "relative" }}>
            <motion.div style={linkStyle} onClick={() => toggleDropdown("categories")} whileHover={linkHoverStyle} transition={{ duration: 0.15 }}>
              Browse
            </motion.div>
            <AnimatePresence>
              {showDropdown === "categories" && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.18 }} style={dropdownContainerStyle}>
                  <Link to="/category/child"  style={dropdownItemStyle} onClick={() => setShowDropdown(null)} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>Pediatric Clinical Studies</Link>
                  <Link to="/category/adult"  style={dropdownItemStyle} onClick={() => setShowDropdown(null)} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>Adult Clinical Studies</Link>
                  <Link to="/category/animal" style={dropdownItemStyle} onClick={() => setShowDropdown(null)} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>Animal Models</Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Heatmap */}
          <motion.div whileHover={linkHoverStyle} transition={{ duration: 0.15 }}>
            <Link to="/heatmap" style={linkStyle}>Heatmap</Link>
          </motion.div>

          {/* About */}
          <div style={{ position: "relative" }}>
            <motion.div style={linkStyle} onClick={() => toggleDropdown("about")} whileHover={linkHoverStyle} transition={{ duration: 0.15 }}>
              About
            </motion.div>
            <AnimatePresence>
              {showDropdown === "about" && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.18 }} style={dropdownContainerStyle}>
                  <Link to="/about/seizurebiomedb" style={dropdownItemStyle} onClick={() => setShowDropdown(null)} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>About the Database</Link>
                  <Link to="/about/statistics"    style={dropdownItemStyle} onClick={() => setShowDropdown(null)} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>Statistics</Link>
                  <Link to="/about/guide"         style={dropdownItemStyle} onClick={() => setShowDropdown(null)} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>User Guide</Link>
                  <Link to="/about/contact"       style={dropdownItemStyle} onClick={() => setShowDropdown(null)} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>Contact</Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Resources */}
          <div style={{ position: "relative" }}>
            <motion.div style={linkStyle} onClick={() => toggleDropdown("resources")} whileHover={linkHoverStyle} transition={{ duration: 0.15 }}>
              Resources
            </motion.div>
            <AnimatePresence>
              {showDropdown === "resources" && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.18 }} style={dropdownContainerStyle}>
                  <a href="https://gtdb.ecogenomic.org/" target="_blank" rel="noopener noreferrer" style={dropdownItemStyle} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>Genome Taxonomy Database</a>
                  <a href="https://www.ncbi.nlm.nih.gov/taxonomy" target="_blank" rel="noopener noreferrer" style={dropdownItemStyle} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>NCBI Taxonomy</a>
                  <a href="https://mimedb.org/" target="_blank" rel="noopener noreferrer" style={dropdownItemStyle} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>Human Microbial Metabolome Database</a>
                  <a href="https://www.epilepsy.com/" target="_blank" rel="noopener noreferrer" style={dropdownItemStyle} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>Epilepsy.com</a>
                  <a href="https://www.ilae.org/" target="_blank" rel="noopener noreferrer" style={dropdownItemStyle} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>International League Against Epilepsy</a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Favourites */}
          <motion.div whileHover={linkHoverStyle} transition={{ duration: 0.15 }}>
            <Link to="/favorites" style={linkStyle}>Favourites</Link>
          </motion.div>

          {/* Divider */}
          <div style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.15)", margin: "0 0.25rem" }} />

          {/* Auth — Profile dropdown when logged in, Login/Register when not */}
          {currentUser ? (
            <div style={{ position: "relative" }}>
              <motion.div
                style={{ ...linkStyle, display: "flex", alignItems: "center", gap: "0.4rem", background: "rgba(255,255,255,0.08)", borderRadius: "6px" }}
                onClick={() => toggleDropdown("profile")}
                whileHover={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}
                transition={{ duration: 0.15 }}
              >
                <span style={{ width: 26, height: 26, borderRadius: "50%", background: "#0079C0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: "700", color: "#fff", flexShrink: 0 }}>
                  {displayName.charAt(0).toUpperCase()}
                </span>
                {displayName}
              </motion.div>
              <AnimatePresence>
                {showDropdown === "profile" && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.18 }} style={{ ...dropdownContainerStyle, right: 0, left: "auto" }}>
                    <div style={{ padding: "0.6rem 1rem 0.4rem", borderBottom: "1px solid #f1f5f9" }}>
                      <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>Signed in as</div>
                      <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#1e293b" }}>{currentUser.username}</div>
                    </div>
                    <Link to="/profile" style={dropdownItemStyle} onClick={() => setShowDropdown(null)} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>My Profile</Link>
                    {currentUser.is_admin && (
                      <Link to="/admin" style={dropdownItemStyle} onClick={() => setShowDropdown(null)} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>Admin Dashboard</Link>
                    )}
                    <div style={{ height: "1px", background: "#f1f5f9", margin: "0.25rem 0" }} />
                    <button style={{ ...dropdownItemStyle, color: "#ef4444" }} onClick={handleLogout} onMouseOver={e => { e.target.style.background = "#fff5f5"; }} onMouseOut={handleMouseOut}>
                      Sign Out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <>
              <motion.div whileHover={linkHoverStyle} transition={{ duration: 0.15 }}>
                <Link to="/login" style={linkStyle}>Login</Link>
              </motion.div>
              <motion.div whileHover={{ background: "#0079C0", color: "#fff" }} transition={{ duration: 0.15 }}>
                <Link to="/register" style={{ ...linkStyle, border: "1px solid rgba(0,121,192,0.5)", borderRadius: "6px" }}>Register</Link>
              </motion.div>
            </>
          )}
        </nav>
      </motion.header>

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<Search />} />
        <Route path="/study/:id" element={<StudyDetails />} />
        <Route path="/category/:type" element={<Category />} />
        <Route path="/login" element={<Login onLogin={setCurrentUser} />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/register" element={<Register />} />
        <Route path="/favorites" element={<Favorites />} />
        <Route path="/profile" element={<Profile currentUser={currentUser} setCurrentUser={setCurrentUser} />} />
        <Route path="/heatmap" element={<Heatmap />} />
        <Route path="/about/seizurebiomedb" element={<SeizureBiomeDB />} />
        <Route path="/about/statistics" element={<Statistics />} />
        <Route path="/about/guide" element={<Guide />} />
        <Route path="/about/contact" element={<Contact />} />
      </Routes>
    </Router>
  );
}

export default App;
